import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { buildSeoTitle } from "@shared/seoTitle";

interface SeoHeadProps {
  title: string;
  titleAr?: string;
  description: string;
  descriptionAr?: string;
  path: string;
  image?: string;
  imageAlt?: string;
  imageAltAr?: string;
  type?: "website" | "article" | "profile";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  noindex?: boolean;
  keywords?: string;
  keywordsAr?: string;
}

const BASE_URL = "https://bellaroseclinics.ae";
const DEFAULT_IMAGE = "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/bella-rose-og-image_default.jpg";
const SITE_NAME_EN = "Bella Rose Medical Center";
const SITE_NAME_AR = "مركز بيلا روز الطبي";
const TWITTER_HANDLE = "@bellarosemc_ae";

export function SeoHead({
  title,
  titleAr,
  description,
  descriptionAr,
  path,
  image = DEFAULT_IMAGE,
  imageAlt,
  imageAltAr,
  type = "website",
  publishedTime,
  modifiedTime,
  author,
  section,
  noindex = false,
  keywords,
  keywordsAr,
}: SeoHeadProps) {
  const { lang } = useLanguage();

  const currentTitle = lang === "ar" && titleAr ? titleAr : title;
  const currentDescription = lang === "ar" && descriptionAr ? descriptionAr : description;
  const siteName = lang === "ar" ? SITE_NAME_AR : SITE_NAME_EN;
  const titleLanguage = /[\u0600-\u06ff]/.test(currentTitle) ? "ar" : "en";
  const fullTitle = buildSeoTitle(currentTitle, path, titleLanguage);
  // Ensure consistent URL format: homepage = BASE_URL/, others = BASE_URL/path (no trailing slash)
  const canonicalUrl = path === "/" ? `${BASE_URL}/` : `${BASE_URL}${path}`;
  const locale = lang === "ar" ? "ar_AE" : "en_AE";
  const alternateLocale = lang === "ar" ? "en_AE" : "ar_AE";
  const currentImageAlt = lang === "ar" && imageAltAr ? imageAltAr : (imageAlt || currentTitle);
  const currentKeywords = lang === "ar" && keywordsAr ? keywordsAr : keywords;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to set or create meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Helper to set or create link tag
    const setLink = (rel: string, href: string, attrs?: Record<string, string>) => {
      const selector = attrs
        ? `link[rel="${rel}"][hreflang="${attrs.hreflang || ""}"]`
        : `link[rel="${rel}"]`;
      let el = document.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      if (attrs) {
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
      }
    };

    // Basic meta
    setMeta("name", "description", currentDescription);
    if (currentKeywords) {
      setMeta("name", "keywords", currentKeywords);
    }
    if (noindex) {
      setMeta("name", "robots", "noindex, nofollow");
    } else {
      setMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    }

    // Open Graph — Core
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", currentDescription);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:site_name", siteName);
    setMeta("property", "og:locale", locale);
    setMeta("property", "og:locale:alternate", alternateLocale);

    // Open Graph — Image
    setMeta("property", "og:image", image);
    setMeta("property", "og:image:secure_url", image);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:type", "image/jpeg");
    setMeta("property", "og:image:alt", currentImageAlt);

    // Article-specific OG
    if (type === "article") {
      if (publishedTime) setMeta("property", "article:published_time", publishedTime);
      if (modifiedTime) setMeta("property", "article:modified_time", modifiedTime);
      if (author) setMeta("property", "article:author", author);
      if (section) setMeta("property", "article:section", section);
      setMeta("property", "article:publisher", "https://www.facebook.com/bellarosemc.ae");
    }

    // Twitter Cards — Full metadata
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:site", TWITTER_HANDLE);
    setMeta("name", "twitter:creator", TWITTER_HANDLE);
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", currentDescription);
    setMeta("name", "twitter:image", image);
    setMeta("name", "twitter:image:alt", currentImageAlt);

    // Canonical URL
    setLink("canonical", canonicalUrl);

    // The current bilingual toggle uses one URL. Do not claim that the same URL
    // is two separate language alternates; retain only the neutral fallback.
    document.querySelectorAll('link[rel="alternate"][hreflang="en"], link[rel="alternate"][hreflang="ar"]').forEach((element) => element.remove());
    setLink("alternate", canonicalUrl, { hreflang: "x-default", rel: "alternate" });

    // Cleanup function
    return () => {
      // We don't remove tags on unmount since the next page will overwrite them
    };
  }, [fullTitle, currentDescription, canonicalUrl, image, currentImageAlt, type, locale, alternateLocale, noindex, publishedTime, modifiedTime, author, section, lang, currentKeywords]);

  return null; // This component only manages document head
}
